<template>
  <section
    class="guest-treatment-selector form"
    :aria-labelledby="`guest-${guestKey}-title`"
  >
    <h3
      :id="`guest-${guestKey}-title`"
      class="guest-treatment-selector__title"
    >
      {{ tc("spaSoft.step1.guestTimeDateSelection.guestFieldLabel", {
        guestNumber: Number(guestKey) + 1,
      }) }}
    </h3>
    <SelectedTreatmentsList
      v-if="guestData.selectedTreatments && guestData.selectedTreatments.length > 0"
    />
    <template v-if="showTreatmentDropdown">
      <div
        :id="`guest-${guestKey}-subtitle`"
        class="guest-treatment-selector__add-treatment-label"
      >
        {{ tc('spaSoft.step1.guestTimeDateSelection.guestSubtitle') }}
      </div>
      <div class="flex-grid-3-col">
        <div class="input dropdown">
          <select
            :id="`guest-${guestKey}-treatment-category`"
            :ref="`guest-${guestKey}-treatment-category`"
            v-model="selectedTreatmentCategory"
            :name="`guest-${guestKey}-treatment-category`"
            class=""
            data-field-type="DropdownList"
            :aria-labelledby="`guest-${guestKey}-treatment-label`"
            :aria-describedby="`guest-${guestKey}-subtitle`"
          >
            <option
              v-for="treatmentCategory in validCategories"
              :key="treatmentCategory.spaSoftId"
              :value="treatmentCategory"
            >
              {{ treatmentCategory.title }}
            </option>
          </select>

          <label
            :id="`guest-${guestKey}-treatment-label`"
            :for="`guest-${guestKey}-treatment-category`"
            class="floating select-label"
          >
            {{ tc('spaSoft.step1.guestTimeDateSelection.treatmentType') }}
          </label>
        </div>
      </div>
    </template>

    <TreatmentsList
      v-if="availableTreatmentsList.length"
      :items="availableTreatmentsList"
      :current-guest="guestKey"
      :selected-category="selectedCategoryTitle"
      :aria-labelledby="`guest-${guestKey}-title`"
      @treatment-selected="selectedTreatmentCategory = null"
    />
    <hr class="spacer">
  </section>
</template>

<script>
  import { mapActions, mapGetters, mapState } from 'vuex'
  import Tracking from '@mixins/tracking'
  import TreatmentsList from './treatments-list/treatments-list.vue'
  import SelectedTreatmentsList from './selected-treatments-list/selected-treatments-list.vue'

  export default {
    name: 'GuestTreatmentSelector',
    components: {
      TreatmentsList,
      SelectedTreatmentsList
    },
    mixins: [Tracking],
    provide() {
      return {
        currentGuestNumber: this.guestKey
      }
    },
    props: {
      guestData: {
        type: Object,
        required: true
      },
      guestKey: {
        type: Number,
        required: true
      }
    },
    data: () => ({
      selectedTreatmentCategory: null,
    }),
    computed: {
      ...mapState('global', ['lang']),
      ...mapState('spaSoft', ['validCategories', 'validTreatments', 'propertyData', 'hotelCode']),
      ...mapGetters('spaSoft', ['numberOfGuests']),
      ...mapGetters('i18n', ['tc']),
      ...mapGetters('currency', ['orSelected', 'getDisplayPrice']),
      availableTreatmentsList() {
        if (this.selectedTreatmentCategory && this.selectedTreatmentCategory.spaSoftId) {
          const { spaSoftId } = this.selectedTreatmentCategory
          return this.validTreatments[spaSoftId] || []
        }
        return []
      },
      selectedCategoryTitle() {
        return this.selectedTreatmentCategory ? this.selectedTreatmentCategory.title : ''
      },
      showTreatmentDropdown() {
        return this.guestData?.selectedTreatments?.length
          < Number(this.propertyData?.maxTreatmentsGuest)
      }
    },
    watch: {
      async selectedTreatmentCategory(val) {
        if (val && !this.validTreatments[val.spaSoftId]) {
          await this.getAvailableTreatmentsByCategoryId({
            categoryId: val.spaSoftId
          })
        }
        this.updateGuestSelectedCategory({ guest: this.guestKey, selectedCategory: val })
      },
      availableTreatmentsList(val) {
        if (val.length) {
          setTimeout(() => {
            if (this.$refs[`guest-${this.guestKey}-treatment-category`]) {
              this.$refs[`guest-${this.guestKey}-treatment-category`].focus()
              this.$refs[`guest-${this.guestKey}-treatment-category`].scrollIntoView({ behavior: 'instant', block: 'center' })
            }
          }, 100)
          this.$track(this.getTrackingData('spaSoft', null, {
            event: 'spaProductImpression',
            stepNumber: 1.1,
            hotelCode: this.hotelCode,
            products: val.flatMap((treatment, index) => (
              treatment.propertyFacilitySpaTreatmentsVariantModel.map((variant) => ({
                productType: 'Treatment',
                treatmentId: variant?.spaSoftId || null,
                treatmentName: treatment?.title || null,
                category: this.selectedCategoryTitle || null,
                price: this.getDisplayPrice({
                  price: variant?.apiTreatmentDetails?.price?.totalCalculated,
                  fromCurrency: this.propertyData?.currency?.isoCode,
                  toCurrency: this.orSelected(this.propertyData?.currency?.isoCode),
                  noSymbol: true
                }),
                currency: this.orSelected(this.propertyData?.currency?.isoCode),
                duration: variant?.duration || null,
                listPosition: index + 1,
              }))
            ))
          }))
        }
      }
    },
    mounted() {
      this.selectedTreatmentCategory = this.guestData?.selectedTreatmentCategory
    },
    methods: {
      ...mapActions('spaSoft', [
        'getAvailableTreatmentsByCategoryId',
        'updateGuestSelectedCategory'
      ]),
    },
  }
</script>
